import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FileSpreadsheet, Users, UserPlus, Shield } from 'lucide-react';
import * as XLSX from 'xlsx';

const templates = [
  {
    id: 'employee',
    title: 'Employee Census Template',
    description: 'Complete employee information including demographics, compensation, and contact details',
    icon: Users,
    filename: 'employee-census-template.xlsx',
    color: 'text-blue-600'
  },
  {
    id: 'dependent',
    title: 'Dependent Census Template',
    description: 'Information for employee dependents including spouses, children, and other qualified dependents',
    icon: UserPlus,
    filename: 'dependent-census-template.xlsx',
    color: 'text-green-600'
  },
  {
    id: 'cobra',
    title: 'COBRA Census Template',
    description: 'COBRA participants and continuation coverage information',
    icon: Shield,
    filename: 'cobra-census-template.xlsx',
    color: 'text-purple-600'
  }
];

const generateEmployeeTemplate = () => {
  const headers = [
    'Employee ID', 'First Name', 'Last Name', 'Middle Initial', 'Email', 'Phone',
    'SSN', 'Date of Birth', 'Gender', 'Marital Status', 'Hire Date', 'Termination Date',
    'Job Title', 'Department', 'Employment Status', 'Salary/Wages', 'Pay Frequency',
    'Work State', 'Home Address Line 1', 'Home Address Line 2', 'Home City', 
    'Home State', 'Home Zip', 'Emergency Contact Name', 'Emergency Contact Phone'
  ];
  
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Census');
  
  return workbook;
};

const generateDependentTemplate = () => {
  const headers = [
    'Employee ID', 'First Name', 'Last Name', 'Middle Initial', 'Date of Birth',
    'Gender', 'Relationship', 'Student Status', 'SSN', 'Disabled'
  ];
  
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Dependent Census');
  
  return workbook;
};

const generateCobraTemplate = () => {
  const headers = [
    'First Name', 'Last Name', 'SSN', 'Date of Birth', 'Gender',
    'Qualifying Event', 'Qualifying Event Date', 'Coverage Type',
    'Coverage Termination Date', 'COBRA Election Date', 'Monthly Premium'
  ];
  
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'COBRA Census');
  
  return workbook;
};

export const CensusTemplateDownload = () => {
  const handleDownload = (template: typeof templates[0]) => {
    console.log(`Generating ${template.filename}`);
    
    let workbook;
    
    switch (template.id) {
      case 'employee':
        workbook = generateEmployeeTemplate();
        break;
      case 'dependent':
        workbook = generateDependentTemplate();
        break;
      case 'cobra':
        workbook = generateCobraTemplate();
        break;
      default:
        console.error('Unknown template type');
        return;
    }
    
    // Generate and download the Excel file
    XLSX.writeFile(workbook, template.filename);
  };

  return (
    <div className="space-y-4">
      <div className="bg-primary/5 border border-primary/10 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium text-foreground mb-1">Template Instructions</h3>
            <p className="text-sm text-muted-foreground">
              Download the templates below, fill them out with your company's information, and upload them back to the document section. 
              These templates are formatted to match PEO requirements.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
        {templates.map((template) => {
          const Icon = template.icon;
          return (
            <Card key={template.id} className="relative overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary">
                    <Icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{template.title}</CardTitle>
                  </div>
                </div>
                <CardDescription className="text-sm">
                  {template.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Button 
                  onClick={() => handleDownload(template)}
                  className="w-full"
                  variant="default"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="bg-muted/50 border border-border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center mt-0.5">
            <span className="text-primary-foreground text-xs font-bold">!</span>
          </div>
          <div>
            <h3 className="font-medium text-foreground mb-1">Important Note</h3>
            <p className="text-sm text-muted-foreground">
              These templates contain all the standard fields required by most PEOs. Some PEO providers may have 
              additional requirements. Your account manager will notify you if any additional information is needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};